'use client';

import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Download, Send, Save, Clock, ChevronDown, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { invoicesService, type Invoice, type InvoiceLineItem } from '@/lib/services/staffingService';

const CLIENTS = [
  { id: 'c1', name: 'Sunrise Medical Center', email: 'billing@sunrisemedical.com', address: '1200 Healthcare Blvd, Chicago, IL 60601' },
  { id: 'c2', name: 'Northside Community Hospital', email: 'ap@northsidehospital.org', address: '450 North Ave, Milwaukee, WI 53202' },
  { id: 'c3', name: 'Lakeside Rehab Facility', email: 'finance@lakesiderehab.com', address: '88 Lakeview Dr, Indianapolis, IN 46201' },
  { id: 'c4', name: 'Greenfield Pediatric Clinic', email: 'billing@greenfieldpeds.com', address: '300 Elm Street, Columbus, OH 43215' },
  { id: 'c5', name: 'Metro Urgent Care Network', email: 'billing@metrougent.com', address: '900 Metro Pkwy, Detroit, MI 48201' },
];

const ROLES = ['RN', 'CNA', 'LPN', 'NP', 'MD', 'PA', 'RT'];

const RATE_BY_ROLE: Record<string, number> = {
  RN: 65, CNA: 28, LPN: 42, NP: 95, MD: 145, PA: 88, RT: 52,
};

type DraftLineItem = Omit<InvoiceLineItem, 'id' | 'invoiceId' | 'amount'> & { tempId: string };

function generateTempId() {
  return `temp-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
}

function generateInvoiceNumber() {
  const year = new Date().getFullYear();
  const num = String(Math.floor(Math.random() * 900) + 100);
  return `INV-${year}-${num}`;
}

interface InvoiceBuilderProps {
  editingInvoice: Invoice | null;
  onBack: () => void;
  onSaved: (invoice: Invoice) => void;
}

export default function InvoiceBuilder({ editingInvoice, onBack, onSaved }: InvoiceBuilderProps) {
  const today = new Date().toISOString().split('T')[0];
  const thirtyDaysOut = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  const [selectedClientId, setSelectedClientId] = useState('');
  const [periodStart, setPeriodStart] = useState(today.slice(0, 8) + '01');
  const [periodEnd, setPeriodEnd] = useState(today);
  const [issueDate, setIssueDate] = useState(today);
  const [dueDate, setDueDate] = useState(thirtyDaysOut);
  const [notes, setNotes] = useState('');
  const [taxRate, setTaxRate] = useState(0);
  const [lineItems, setLineItems] = useState<DraftLineItem[]>([]);
  const [showTrackedHours, setShowTrackedHours] = useState(false);
  const [approvedTimesheets, setApprovedTimesheets] = useState<{ id: string; staffName: string; role: string; shiftDate: string; regularHours: number; otHours: number }[]>([]);
  const [loadingTimesheets, setLoadingTimesheets] = useState(false);
  const [selectedTracked, setSelectedTracked] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'sent' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [invoiceNumber] = useState(() => editingInvoice?.invoiceNumber || generateInvoiceNumber());

  // Pre-fill if editing
  useEffect(() => {
    if (editingInvoice) {
      const client = CLIENTS.find((c) => c.name === editingInvoice.client);
      setSelectedClientId(client?.id ?? '');
      setPeriodStart(editingInvoice.periodStart);
      setPeriodEnd(editingInvoice.periodEnd);
      setIssueDate(editingInvoice.issueDate);
      setDueDate(editingInvoice.dueDate);
      setNotes(editingInvoice.notes);
      setTaxRate(editingInvoice.taxRate);
      setLineItems(
        editingInvoice.lineItems.map((li) => ({
          tempId: li.id,
          timesheetId: li.timesheetId,
          staffName: li.staffName,
          role: li.role,
          shiftDate: li.shiftDate,
          hours: li.hours,
          rate: li.rate,
          description: li.description,
        }))
      );
    }
  }, [editingInvoice]);

  // Load approved timesheets when panel opens
  useEffect(() => {
    if (!showTrackedHours) return;
    setLoadingTimesheets(true);
    invoicesService.getApprovedTimesheets(periodStart, periodEnd).then((ts) => {
      setApprovedTimesheets(
        ts.map((t) => ({
          id: t.id,
          staffName: t.staffName,
          role: t.role.toUpperCase(),
          shiftDate: t.shiftDate,
          regularHours: t.regularHours,
          otHours: t.otHours,
        }))
      );
      setLoadingTimesheets(false);
    });
  }, [showTrackedHours, periodStart, periodEnd]);

  const selectedClient = CLIENTS.find((c) => c.id === selectedClientId);

  const addBlankLineItem = () => {
    setLineItems((prev) => [
      ...prev,
      { tempId: generateTempId(), timesheetId: null, staffName: '', role: 'RN', shiftDate: today, hours: 8, rate: 65, description: '' },
    ]);
  };

  const updateLineItem = (tempId: string, field: keyof DraftLineItem, value: string | number | null) => {
    setLineItems((prev) =>
      prev.map((item) => {
        if (item.tempId !== tempId) return item;
        const updated = { ...item, [field]: value };
        if (field === 'role') {
          updated.rate = RATE_BY_ROLE[value as string] ?? updated.rate;
        }
        return updated;
      })
    );
  };

  const removeLineItem = (tempId: string) => {
    setLineItems((prev) => prev.filter((item) => item.tempId !== tempId));
  };

  const importTrackedHours = () => {
    const toImport = approvedTimesheets
      .filter((ts) => selectedTracked.has(ts.id))
      .map((ts) => ({
        tempId: generateTempId(),
        timesheetId: ts.id,
        staffName: ts.staffName,
        role: ts.role,
        shiftDate: ts.shiftDate,
        hours: ts.regularHours + ts.otHours,
        rate: RATE_BY_ROLE[ts.role] ?? 65,
        description: `Approved shift — ${ts.role}`,
      }));
    setLineItems((prev) => [...prev, ...toImport]);
    setSelectedTracked(new Set());
    setShowTrackedHours(false);
  };

  const toggleTrackedRow = (id: string) => {
    setSelectedTracked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllTracked = () => {
    if (selectedTracked.size === approvedTimesheets.length) {
      setSelectedTracked(new Set());
    } else {
      setSelectedTracked(new Set(approvedTimesheets.map((ts) => ts.id)));
    }
  };

  const subtotal = lineItems.reduce((sum, item) => sum + item.hours * item.rate, 0);
  const taxAmount = (subtotal * taxRate) / 100;
  const total = subtotal + taxAmount;

  const buildPayload = (status: 'draft' | 'sent') => ({
    invoiceNumber,
    client: selectedClient?.name ?? '',
    clientEmail: selectedClient?.email ?? '',
    clientAddress: selectedClient?.address ?? '',
    periodStart,
    periodEnd,
    issueDate,
    dueDate,
    notes,
    status,
    taxRate,
    subtotal,
    taxAmount,
    total,
  });

  const handleSave = async (status: 'draft' | 'sent') => {
    if (!selectedClient) {
      setSaveError('Please select a client before saving.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const payload = buildPayload(status);
      const linePayload = lineItems.map((li) => ({
        timesheetId: li.timesheetId,
        staffName: li.staffName,
        role: li.role,
        shiftDate: li.shiftDate,
        hours: li.hours,
        rate: li.rate,
        description: li.description,
      }));

      let saved: Invoice | null;
      if (editingInvoice) {
        saved = await invoicesService.update(editingInvoice.id, payload, linePayload);
      } else {
        saved = await invoicesService.create(payload, linePayload);
      }

      if (saved) {
        setSaveStatus(status === 'sent' ? 'sent' : 'saved');
        setTimeout(() => {
          onSaved(saved!);
        }, 800);
      } else {
        setSaveError('Failed to save invoice. Please try again.');
      }
    } catch (e: unknown) {
      const err = e as { message?: string };
      setSaveError(err?.message || 'Failed to save invoice.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
      {/* Left: Builder */}
      <div className="xl:col-span-2 space-y-5">
        {/* Client & Period */}
        <div className="bg-card border border-border rounded-xl p-5 space-y-4">
          <h2 className="text-sm font-700 text-foreground">Client & Billing Period</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-xs font-600 text-muted-foreground mb-1.5">Client *</label>
              <div className="relative">
                <select
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(e.target.value)}
                  className="w-full appearance-none bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 pr-8"
                >
                  <option value="">Select a client...</option>
                  {CLIENTS.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              </div>
              {selectedClient && (
                <div className="mt-2 p-3 bg-muted/60 rounded-lg text-xs text-muted-foreground space-y-0.5">
                  <p className="font-600 text-foreground">{selectedClient.name}</p>
                  <p>{selectedClient.email}</p>
                  <p>{selectedClient.address}</p>
                </div>
              )}
            </div>
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5">Period Start</label>
              <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)}
                className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5">Period End</label>
              <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)}
                className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5">Issue Date</label>
              <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)}
                className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5">Due Date</label>
              <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
                className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <h2 className="text-sm font-700 text-foreground">Line Items</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowTrackedHours(!showTrackedHours)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-600 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <Clock size={13} />
                Import Approved Hours
              </button>
              <button
                onClick={addBlankLineItem}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 text-primary text-xs font-600 hover:bg-primary/20 transition-colors"
              >
                <Plus size={13} />
                Add Line
              </button>
            </div>
          </div>

          {/* Import Approved Timesheets Panel */}
          {showTrackedHours && (
            <div className="border-b border-border bg-muted/30 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-600 text-foreground">
                  Approved timesheets for {periodStart} – {periodEnd}
                  {!loadingTimesheets && (
                    <span className="ml-2 text-muted-foreground font-400">({approvedTimesheets.length} records)</span>
                  )}
                </p>
                <button
                  onClick={importTrackedHours}
                  disabled={selectedTracked.size === 0}
                  className="px-3 py-1.5 rounded-lg btn-primary text-xs font-600 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Import Selected ({selectedTracked.size})
                </button>
              </div>
              <div className="overflow-x-auto rounded-lg border border-border bg-card">
                {loadingTimesheets ? (
                  <div className="flex items-center justify-center py-6 gap-2 text-muted-foreground text-xs">
                    <Loader2 size={14} className="animate-spin" />
                    Loading approved timesheets...
                  </div>
                ) : (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-border bg-muted/50">
                        <th className="px-3 py-2 text-left">
                          <input type="checkbox"
                            checked={selectedTracked.size === approvedTimesheets.length && approvedTimesheets.length > 0}
                            onChange={selectAllTracked} className="rounded" />
                        </th>
                        <th className="px-3 py-2 text-left font-600 text-muted-foreground">Staff</th>
                        <th className="px-3 py-2 text-left font-600 text-muted-foreground">Role</th>
                        <th className="px-3 py-2 text-left font-600 text-muted-foreground">Date</th>
                        <th className="px-3 py-2 text-left font-600 text-muted-foreground">Reg Hrs</th>
                        <th className="px-3 py-2 text-left font-600 text-muted-foreground">OT Hrs</th>
                        <th className="px-3 py-2 text-right font-600 text-muted-foreground">Est. Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {approvedTimesheets.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-center py-4 text-muted-foreground">
                            No approved timesheets in this period
                          </td>
                        </tr>
                      ) : (
                        approvedTimesheets.map((ts) => {
                          const totalHours = ts.regularHours + ts.otHours;
                          const rate = RATE_BY_ROLE[ts.role] ?? 65;
                          return (
                            <tr key={ts.id} className={`transition-colors ${selectedTracked.has(ts.id) ? 'bg-primary/5' : 'hover:bg-muted/40'}`}>
                              <td className="px-3 py-2">
                                <input type="checkbox" checked={selectedTracked.has(ts.id)}
                                  onChange={() => toggleTrackedRow(ts.id)} className="rounded" />
                              </td>
                              <td className="px-3 py-2 font-500 text-foreground">{ts.staffName}</td>
                              <td className="px-3 py-2">
                                <span className="badge-rn px-1.5 py-0.5 rounded text-[10px] font-600">{ts.role}</span>
                              </td>
                              <td className="px-3 py-2 font-mono-data text-muted-foreground">{ts.shiftDate}</td>
                              <td className="px-3 py-2 font-mono-data">{ts.regularHours}h</td>
                              <td className="px-3 py-2 font-mono-data">{ts.otHours > 0 ? `${ts.otHours}h` : '—'}</td>
                              <td className="px-3 py-2 font-mono-data text-right font-600">${(totalHours * rate).toLocaleString()}</td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* Line Items Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left px-4 py-2.5 text-xs font-600 text-muted-foreground">Staff Name</th>
                  <th className="text-left px-3 py-2.5 text-xs font-600 text-muted-foreground">Role</th>
                  <th className="text-left px-3 py-2.5 text-xs font-600 text-muted-foreground">Date</th>
                  <th className="text-left px-3 py-2.5 text-xs font-600 text-muted-foreground">Description</th>
                  <th className="text-right px-3 py-2.5 text-xs font-600 text-muted-foreground">Hours</th>
                  <th className="text-right px-3 py-2.5 text-xs font-600 text-muted-foreground">Rate/hr</th>
                  <th className="text-right px-3 py-2.5 text-xs font-600 text-muted-foreground">Amount</th>
                  <th className="px-3 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {lineItems.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-10 text-muted-foreground text-sm">
                      <p className="mb-2">No line items yet</p>
                      <p className="text-xs">Add manually or import from approved timesheets</p>
                    </td>
                  </tr>
                ) : (
                  lineItems.map((item) => (
                    <tr key={item.tempId} className="group">
                      <td className="px-4 py-2">
                        <input type="text" value={item.staffName}
                          onChange={(e) => updateLineItem(item.tempId, 'staffName', e.target.value)}
                          placeholder="Staff name"
                          className="w-full bg-transparent border-b border-transparent focus:border-primary/50 focus:outline-none text-sm text-foreground placeholder:text-muted-foreground py-0.5" />
                      </td>
                      <td className="px-3 py-2">
                        <select value={item.role}
                          onChange={(e) => updateLineItem(item.tempId, 'role', e.target.value)}
                          className="bg-transparent border-b border-transparent focus:border-primary/50 focus:outline-none text-sm text-foreground py-0.5">
                          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <input type="date" value={item.shiftDate}
                          onChange={(e) => updateLineItem(item.tempId, 'shiftDate', e.target.value)}
                          className="bg-transparent border-b border-transparent focus:border-primary/50 focus:outline-none text-xs text-muted-foreground font-mono-data py-0.5" />
                      </td>
                      <td className="px-3 py-2">
                        <input type="text" value={item.description}
                          onChange={(e) => updateLineItem(item.tempId, 'description', e.target.value)}
                          placeholder="Shift description"
                          className="w-full bg-transparent border-b border-transparent focus:border-primary/50 focus:outline-none text-sm text-foreground placeholder:text-muted-foreground py-0.5" />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input type="number" value={item.hours} min={0} step={0.5}
                          onChange={(e) => updateLineItem(item.tempId, 'hours', parseFloat(e.target.value) || 0)}
                          className="w-16 bg-transparent border-b border-transparent focus:border-primary/50 focus:outline-none text-sm text-right font-mono-data text-foreground py-0.5" />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input type="number" value={item.rate} min={0}
                          onChange={(e) => updateLineItem(item.tempId, 'rate', parseFloat(e.target.value) || 0)}
                          className="w-16 bg-transparent border-b border-transparent focus:border-primary/50 focus:outline-none text-sm text-right font-mono-data text-foreground py-0.5" />
                      </td>
                      <td className="px-3 py-2 text-right font-mono-data font-600 text-foreground text-sm">
                        ${(item.hours * item.rate).toLocaleString()}
                      </td>
                      <td className="px-3 py-2">
                        <button onClick={() => removeLineItem(item.tempId)}
                          className="opacity-0 group-hover:opacity-100 p-1 rounded text-muted-foreground hover:text-danger transition-all">
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-card border border-border rounded-xl p-5">
          <label className="block text-xs font-600 text-muted-foreground mb-2">Notes / Payment Terms</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
            placeholder="e.g. Net 30 payment terms. Please reference invoice number on payment."
            className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none" />
        </div>
      </div>

      {/* Right: Summary & Actions */}
      <div className="xl:col-span-1 space-y-4">
        <div className="bg-card border border-border rounded-xl p-5 space-y-4 sticky top-6">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-700 text-foreground">Invoice Summary</h2>
            <span className="text-xs font-mono-data text-muted-foreground">{invoiceNumber}</span>
          </div>

          <div className="p-3 bg-muted/50 rounded-lg space-y-1">
            <p className="text-xs font-600 text-foreground">{selectedClient?.name || 'No client selected'}</p>
            {selectedClient && (
              <>
                <p className="text-xs text-muted-foreground">{selectedClient.email}</p>
                <p className="text-xs text-muted-foreground">{selectedClient.address}</p>
              </>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <p className="text-muted-foreground mb-0.5">Period</p>
              <p className="font-600 text-foreground font-mono-data">{periodStart || '—'}</p>
              <p className="text-muted-foreground">to {periodEnd || '—'}</p>
            </div>
            <div>
              <p className="text-muted-foreground mb-0.5">Due Date</p>
              <p className="font-600 text-foreground font-mono-data">{dueDate || '—'}</p>
            </div>
          </div>

          <div className="flex items-center justify-between text-xs border-t border-border pt-3">
            <span className="text-muted-foreground">Line items</span>
            <span className="font-600 text-foreground">{lineItems.length}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Total hours</span>
            <span className="font-600 text-foreground font-mono-data">
              {lineItems.reduce((s, i) => s + i.hours, 0).toFixed(1)}h
            </span>
          </div>

          <div className="border-t border-border pt-3 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="font-mono-data font-600 text-foreground">${subtotal.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-1.5">
                <span className="text-muted-foreground">Tax</span>
                <input type="number" value={taxRate} min={0} max={100} step={0.5}
                  onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                  className="w-12 text-xs bg-muted border border-border rounded px-1.5 py-0.5 text-center font-mono-data focus:outline-none focus:ring-1 focus:ring-primary/30" />
                <span className="text-muted-foreground text-xs">%</span>
              </div>
              <span className="font-mono-data text-muted-foreground">${taxAmount.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between text-base font-700 border-t border-border pt-2">
              <span className="text-foreground">Total</span>
              <span className="font-mono-data text-primary">${total.toLocaleString()}</span>
            </div>
          </div>

          {saveError && (
            <div className="flex items-start gap-2 p-2.5 bg-danger/10 border border-danger/20 rounded-lg">
              <AlertCircle size={13} className="text-danger mt-0.5 flex-shrink-0" />
              <p className="text-xs text-danger">{saveError}</p>
            </div>
          )}

          <div className="space-y-2 pt-1">
            <button
              onClick={() => handleSave('sent')}
              disabled={saving}
              className="w-full btn-primary flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-600 disabled:opacity-60"
            >
              {saving ? <Loader2 size={15} className="animate-spin" /> : saveStatus === 'sent' ? <CheckCircle2 size={15} /> : <Send size={15} />}
              {saveStatus === 'sent' ? 'Invoice Sent!' : 'Send Invoice'}
            </button>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => handleSave('draft')}
                disabled={saving}
                className="flex items-center justify-center gap-1.5 py-2 rounded-lg border border-border text-sm font-600 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-60"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : saveStatus === 'saved' ? <CheckCircle2 size={14} className="text-positive" /> : <Save size={14} />}
                {saveStatus === 'saved' ? 'Saved!' : 'Save Draft'}
              </button>
              <button
                onClick={onBack}
                className="flex items-center justify-center gap-1.5 py-2 rounded-lg border border-border text-sm font-600 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <Download size={14} />
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
